import React, { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import './Work.scss';
import gameImage from '../../assets/images/game.png';
import portfolioImage from '../../assets/images/portfolio.png';
import webbcastImage from '../../assets/images/webbcast.png';
import SEO from '../../components/SEO/SEO';

const Work = () => {
  const containerRef = useRef(null);

  const projects = [
    {
      id: 1,
      title: 'Moon Of Doom - A JS based game',
      description: [
        'A simple JS based game where you play as Jan Michael Vincent from Rick and Morty and shoot mindlessly at intergalactic bugs! Uses PhaserJS framework for game physics. Designed all assets in Figma!',
      ],
      screenshot: gameImage,
      url: 'https://moonofdoom.github.io/',
      githubUrl:
        'https://github.com/MoidHasanBeig/theMoonOfDoomVsJanMichaelVincent',
      technologies: ['JavaScript', 'PhaserJS', 'Figma'],
    },
    {
      id: 2,
      title: 'Webbcast - A no signup livestreaming platform',
      description: [
        'A no signup livestreaming platform where you can stream yourself to your audience. Built with React, Node.js, and Socket.io. Uses WebRTC for real-time communication. Designed all assets in Figma!',
      ],
      screenshot: webbcastImage,
      url: 'https://axtrion-broadcast-app.onrender.com/',
      githubUrl: 'https://github.com/MoidHasanBeig/axtrion-broadcast-app',
      technologies: ['React', 'Node.js', 'Socket.io', 'WebRTC', 'Figma'],
    },
    {
      id: 3,
      title: 'My old portfolio',
      description: [
        "My old portfolio website. Built with React and designed all assets in Figma! You'll find a hint of my current website's theme but in 2D.",
      ],
      screenshot: portfolioImage,
      url: 'https://moidhasanbeig.github.io/',
      githubUrl: 'https://github.com/MoidHasanBeig/my-port',
      technologies: ['React', 'Figma'],
    },
    {
      id: 4,
      title: 'More Projects Coming Soon...',
      description: [
        "The adventure continues! I'm currently cooking up some exciting new projects in my digital kitchen.",
        'Stay tuned! 🚀',
      ],
      screenshot: null,
      url: null,
      githubUrl: null,
      technologies: ['Coming Soon', 'Stay Tuned'],
      isPlaceholder: true,
    },
  ];

  useEffect(() => {
    if (!containerRef.current) return;

    const h1 = containerRef.current.querySelector('h1');
    const introP = containerRef.current.querySelector('.work-page-content > p');
    const projectCards = containerRef.current.querySelectorAll(
      '.project-card-wrapper'
    );

    if (!h1 || !introP) return;

    // Set initial state
    gsap.set([h1, introP], {
      opacity: 0,
      y: 30,
    });

    gsap.set(projectCards, {
      opacity: 0,
      y: 50,
    });

    const tl = gsap.timeline();

    // Animate heading
    tl.to(h1, {
      opacity: 1,
      y: 0,
      duration: 0.8,
      ease: 'power3.out',
    });

    // Animate intro paragraph
    tl.to(
      introP,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
      },
      '-=0.6'
    );

    // Animate project cards with stagger
    tl.to(
      projectCards,
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'power3.out',
        stagger: 0.2,
      },
      '-=0.4'
    );

    return () => {
      tl.kill();
    };
  }, []);

  return (
    <>
      <SEO
        title="My Work & Projects - Moid Hasan Beig | Portfolio"
        description="Explore my portfolio projects including Moon Of Doom (JS game), Webbcast (livestreaming platform), and more. Built with React, Node.js, JavaScript, and modern web technologies."
        keywords="Moid Hasan Beig, portfolio projects, web development, React projects, JavaScript games, livestreaming platform, Moon Of Doom, Webbcast, PhaserJS, WebRTC"
        path="/work"
      />
      <div className="work-page" ref={containerRef}>
        <div className="work-page-content">
          <h1>Work</h1>
          <p>
            My{' '}
            <b>
              <i>
                <a
                  href="https://www.linkedin.com/in/moidhasanbeig/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link linkedin-link"
                >
                  LinkedIn
                </a>
              </i>
            </b>{' '}
            covers the main quest!
            <br />
            What follows are the side quests I couldn't resist picking up!
          </p>
          <div className="projects-grid">
            {projects.map(project => (
              <div
                key={project.id}
                className={`project-card-wrapper ${project.isPlaceholder ? 'placeholder-card' : ''}`}
              >
                {project.isPlaceholder ? (
                  <div className="project-card placeholder-card">
                    <div className="project-screenshot placeholder-screenshot">
                      <div className="placeholder-content">
                        <span className="placeholder-icon">🚧</span>
                      </div>
                    </div>
                    <div className="project-info">
                      <div className="project-title-container">
                        <h2>{project.title}</h2>
                      </div>
                      <div className="project-description">
                        {project.description.map((line, index) => (
                          <p key={index}>{line}</p>
                        ))}
                      </div>
                      {project.technologies &&
                        project.technologies.length > 0 && (
                          <div className="project-technologies">
                            {project.technologies.map((tech, index) => (
                              <span key={index} className="tech-badge">
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>
                ) : (
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project-card link"
                  >
                    <div className="project-screenshot link">
                      <img
                        src={project.screenshot}
                        alt={project.title}
                        className="link"
                      />
                    </div>
                    <div className="project-info link">
                      <div className="project-title-container">
                        <h2 className="link">{project.title}</h2>
                        {project.githubUrl && (
                          <a
                            href={project.githubUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="github-badge link"
                            onClick={e => e.stopPropagation()}
                          >
                            <span className="github-badge-icon link">
                              <div className="github-icon link" />
                            </span>
                            <span className="github-badge-text link">
                              Github
                            </span>
                          </a>
                        )}
                      </div>
                      <div className="project-description">
                        {project.description.map((line, index) => (
                          <p key={index} className="link">
                            {line}
                          </p>
                        ))}
                      </div>
                      {project.technologies &&
                        project.technologies.length > 0 && (
                          <div className="project-technologies">
                            {project.technologies.map((tech, index) => (
                              <span key={index} className="tech-badge link">
                                {tech}
                              </span>
                            ))}
                          </div>
                        )}
                    </div>
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default Work;
